/**
 * Redux slice tracking the pending-row count for the Quick Review board.
 *
 * The count is shared in the store so any number of places (side-nav
 * badge, board page, …) can read the same loading/error/data state.
 * @module quickReviewCountSlice
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { QuickReviewService } from "../../../api/services/quickReviewService";
import { QuickReviewCountResponse } from "../../../data-model/quickReviewModel";

/** Loading/error/data state for the Quick Review board pending-row count. */
export interface QuickReviewCountState {
  /** True while the count endpoint request is in flight */
  loading: boolean;
  /** Safe error message when the count request fails */
  error: string | null;
  /** Board-wide pending row count from the count endpoint */
  data: QuickReviewCountResponse | null;
}

const initialState: QuickReviewCountState = {
  loading: false,
  error: null,
  data: null,
};

export const quickReviewCountSlice = createSlice({
  name: "quickReviewCount",
  initialState,
  reducers: {
    quickReviewCountFetchStart: (state) => {
      state.loading = true;
      state.error = null;
      state.data = null;
    },
    quickReviewCountFetchSuccess: (
      state,
      action: PayloadAction<QuickReviewCountResponse>,
    ) => {
      state.loading = false;
      state.error = null;
      state.data = action.payload;
    },
    quickReviewCountFetchFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.data = null;
    },
    resetQuickReviewCount: (state) => {
      state.loading = false;
      state.error = null;
      state.data = null;
    },
  },
});

export const {
  quickReviewCountFetchStart,
  quickReviewCountFetchSuccess,
  quickReviewCountFetchFailure,
  resetQuickReviewCount,
} = quickReviewCountSlice.actions;

/**
 * Thunk: fetches the total number of pending Quick Review board rows for
 * the caller's org. Skips the request when one is already in flight.
 */
export const fetchQuickReviewCount =
  () => async (dispatch: any, getState: any) => {
    if (getState().quickReviewCount.loading) return;

    dispatch(quickReviewCountFetchStart());
    try {
      const data = await QuickReviewService.count();
      dispatch(quickReviewCountFetchSuccess(data));
    } catch (error) {
      dispatch(
        quickReviewCountFetchFailure(
          error instanceof Error ? error.message : String(error),
        ),
      );
    }
  };

export default quickReviewCountSlice.reducer;
